import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { tagHotjarRecording } from '../../helpers/util'
import useAuth from '../../hooks/useAuth'
import useRequest from '../../hooks/useRequest'
import ContentLayout from '../../components/ContentLayout/ContentLayout'
import MainHeading from '../../components/MainHeading/MainHeading'
import { DocumentAddIcon } from '@heroicons/react/outline'
import Button from '../../components/Button/Button'
import TabPanel from '../../components/TabPanel/TabPanel'
import {
    formatRequestsListTable,
    formatEstimateListTable,
} from '../../helpers/tableUtils'
import useFilterModal from '../../hooks/useFilterModal'
import PropTypes from 'prop-types'
import AppRoutes from '../../routes/appRoutes'
import RequestTable from './RequestTable'
import EstimateTable from './EstimateTable'
import {
    getInitialFilters,
    getInitialEstimateFilters,
} from '../../helpers/requestsFilters'
import { parse } from 'qs'
import { saveRequests, getRequestsByType } from '../../../../indexedDb/requests'
import { useFetchState } from '../../context/FetchStateContext'
import { getPendingRequests } from '../../../../indexedDb/pendingRequests'
import SuccessActionMessage from '../../components/SuccessActionMessage/SuccessActionMessage'
import { useLocation } from 'react-router-dom'

const RequestsPage = ({ history }) => {
    const {
        closedRequests,
        openRequests,
        estimateRequests,
        saveFilters,
        saveEstimateFilters,
        savePastRequestsFilters,
        filters,
        pastRequestsFilters,
        estimateFilters,
        getAllRequests,
        changeNeedsUpdate,
        needsUpdateRequest,
        needsUpdateDashboard,
    } = useRequest()

    const location = useLocation()
    const filterByType = location.state?.filterByType
    const filterByStatus = location.state?.filterByStatus
    const [filterOpen, setFilterOpen] = useState(false)
    const [estimateFilterOpen, setEstimateFilterOpen] = useState(false)
    const [pendingRequests, setPendingRequests] = useState([])

    const { user } = useAuth()
    const locPrefs = user?.locationPreferences
    const windowParams = parse(window.location.search, {
        ignoreQueryPrefix: true,
    })
    const hasParams = Object.keys(windowParams || [])?.length > 0
    const { setIsFetching, setDataFetched } = useFetchState()

    const tab = windowParams?.activeTab
    const firstTab = { key: tab || 'activeRequests', active: true }

    const allFilters = getInitialFilters(
        locPrefs,
        windowParams,
        'activeTabFilters',
    )
    const hasStoreFilters = filters?.length > 0
    const filtersToUse = hasStoreFilters && !hasParams ? filters : allFilters

    const allPastFilters = getInitialFilters(
        locPrefs,
        windowParams,
        'pastTabFilters',
    )
    const hasPastStoreFilters = pastRequestsFilters?.length > 0
    const pastFiltersToUse =
        hasPastStoreFilters && !hasParams ? pastRequestsFilters : allPastFilters

    const allEstimateFilters = getInitialEstimateFilters(locPrefs, windowParams)
    const hasStoreEstimateFilters = estimateFilters?.length > 0
    const estimateFiltersToUse =
        hasStoreEstimateFilters && !hasParams
            ? estimateFilters
            : allEstimateFilters

    useEffect(() => {
        const fetchPendingRequests = async () => {
            try {
                const fetchedPendingRequests = await getPendingRequests()
                setPendingRequests(fetchedPendingRequests)
            } catch (error) {
                console.error('Error fetching pending requests:', error)
            }
        }

        fetchPendingRequests()
    }, [])

    const {
        filteredData: filteredOpenRequests,
        onFilter: onFilterOpenRequests,
        setFilteredData: setFilterOpenRequests,
    } = useFilterModal(
        formatRequestsListTable,
        [...pendingRequests, ...openRequests],
        saveFilters,
    )

    const {
        filteredData: filteredClosedRequests,
        onFilter: onFilterClosedRequests,
        setFilteredData: setFilterClosedRequests,
    } = useFilterModal(
        formatRequestsListTable,
        closedRequests,
        savePastRequestsFilters,
    )

    const {
        filteredData: filteredEstimateRequests,
        onFilter: onFilterEstimateRequests,
        setFilteredData: setFilterEstimateRequests,
    } = useFilterModal(
        formatEstimateListTable,
        estimateRequests,
        saveEstimateFilters,
    )

    async function fetchRequests(type) {
        try {
            const storedOpenRequests = await getRequestsByType('openRequest')
            const storedClosedRequests = await getRequestsByType(
                'closedRequest',
            )
            const storedEstimateRequests = await getRequestsByType(
                'estimatedRequest',
            )

            let mergeRequests = [...pendingRequests, ...storedOpenRequests]

            if (filterByType === 'Repair') {
                mergeRequests = mergeRequests.filter(
                    request => request.showType === 'Repair',
                )
            }

            if (mergeRequests.length > 0) {
                const filteredOpenRequests = mergeRequests.filter(request =>
                    locPrefs.some(
                        location =>
                            String(location.id) === String(request.shipToId) ||
                            String(location.value) ===
                            String(request.shipToAccountId),
                    ),
                )
                setFilterOpenRequests(
                    formatRequestsListTable(
                        filteredOpenRequests,
                        'openRequest',
                    ),
                )
            }
            if (storedClosedRequests.length) {
                let filteredClosed = storedClosedRequests.filter(request =>
                    locPrefs.some(
                        location =>
                            String(location.id) === String(request.shipToId),
                    ),
                )

                if (filterByType === 'Awaiting PO') {
                    filteredClosed = filteredClosed.filter(
                        request => request.status === 'Awaiting PO',
                    )
                }

                setFilterClosedRequests(
                    formatRequestsListTable(filteredClosed, 'closedRequest'),
                )
            }
            if (storedEstimateRequests.length) {
                let filteredEstimatesRequests = storedEstimateRequests.filter(
                    request =>
                        locPrefs.some(
                            location =>
                                String(location.id) ===
                                String(request.shipToId),
                        ),
                )

                // Apply status filter from navigation state
                if (filterByStatus) {
                    filteredEstimatesRequests = filteredEstimatesRequests.filter(
                        request => request.status === filterByStatus,
                    )
                }

                setFilterEstimateRequests(
                    formatEstimateListTable(
                        filteredEstimatesRequests,
                        'estimatedRequest',
                    ),
                )
            }
            setIsFetching(false)
            setDataFetched(true)
        } catch (error) {
            console.error(
                `Error fetching ${type} requests from IndexedDB:`,
                error,
            )
            setIsFetching(false)
            setDataFetched(true)
        }
    }

    useEffect(() => {
        if (needsUpdateRequest) {
            setIsFetching(true)
            const locPreferencesId = locPrefs.map(item => `${item?.id}`)
            getAllRequests(locPreferencesId)
            changeNeedsUpdate({
                dashboard: needsUpdateDashboard,
                request: false,
            })
        }
        fetchRequests()
    }, [needsUpdateRequest, locPrefs, pendingRequests])

    useEffect(() => {
        tagHotjarRecording('Request List')
    }, [])

    useEffect(() => {
        let mergeRequests = [...pendingRequests, ...openRequests]
        if (filterByType === 'Repair') {
            mergeRequests = mergeRequests.filter(
                request => request.showType === 'Repair',
            )
        }

        if (mergeRequests.length > 0) {
            const formattedMergedRequests = formatRequestsListTable(
                mergeRequests,
            )
            setFilterOpenRequests(formattedMergedRequests)

            const formattedOpenRequests = formatRequestsListTable(openRequests)
            saveRequests(formattedOpenRequests, 'openRequest')
        }
    }, [openRequests, pendingRequests, filterByType])

    useEffect(() => {
        if (closedRequests?.length > 0) {
            const formattedClosedRequests = formatRequestsListTable(
                closedRequests,
            )
            setFilterClosedRequests(formattedClosedRequests)
            saveRequests(formattedClosedRequests, 'closedRequest')
        }
    }, [closedRequests])

    useEffect(() => {
        if (estimateRequests?.length > 0) {
            let estimatesToFormat = estimateRequests

            // Apply status filter from navigation state
            if (filterByStatus) {
                estimatesToFormat = estimateRequests.filter(
                    request => request.status === filterByStatus,
                )
            }

            const formattedEstimateRequests = formatEstimateListTable(
                estimatesToFormat,
            )
            setFilterEstimateRequests(formattedEstimateRequests)
            saveRequests(
                formatEstimateListTable(estimateRequests),
                'estimatedRequest',
            )
        }
    }, [estimateRequests, filterByStatus])

    useEffect(() => {
        if (filtersToUse?.length > 0 && openRequests?.length > 0) {
            onFilterOpenRequests(filtersToUse)
        }
        if (pastFiltersToUse?.length > 0 && closedRequests?.length > 0) {
            onFilterClosedRequests(pastFiltersToUse)
        }
        if (estimateFiltersToUse?.length > 0 && estimateRequests?.length > 0) {
            onFilterEstimateRequests(estimateFiltersToUse)
        }
    }, [openRequests, closedRequests, estimateRequests])

    const handleTabChange = () => {
        setFilterOpen(false)
        setEstimateFilterOpen(false)
        fetchRequests()
    }

    const memoTableActive = useMemo(
        () => (
            <RequestTable
                filtersLIst={filtersToUse}
                data={filteredOpenRequests}
                onFilter={onFilterOpenRequests}
                title="Active Requests Details"
                subtitle={
                    <span>
                        You have a total of{' '}
                        <b className="text-black90">
                            {filteredOpenRequests?.length}
                        </b>{' '}
                        active requests
                    </span>
                }
                tabFilters="activeTabFilters"
            />
        ),
        [filteredOpenRequests, filterOpen, filtersToUse],
    )

    const memoTableClosed = useMemo(
        () => (
            <RequestTable
                filtersLIst={pastFiltersToUse}
                data={filteredClosedRequests}
                onFilter={onFilterClosedRequests}
                title="Past Requests Details"
                tabFilters="pastTabFilters"
                subtitle={
                    <span>
                        You have a total of{' '}
                        <b className="text-black90">
                            {filteredClosedRequests?.length}
                        </b>{' '}
                        past requests
                    </span>
                }
            />
        ),
        [filteredClosedRequests, filterOpen, pastFiltersToUse],
    )

    const memoTableEstimate = useMemo(
        () => (
            <EstimateTable
                filtersLIst={estimateFiltersToUse}
                data={filteredEstimateRequests}
                onFilter={onFilterEstimateRequests}
                title="Service Estimate Details"
                subtitle={
                    <span>
                        You have a total of{' '}
                        <b className="text-black90">
                            {filteredEstimateRequests?.length}
                        </b>{' '}
                        service estimates
                    </span>
                }
            />
        ),
        [filteredEstimateRequests, estimateFilterOpen, estimateFiltersToUse],
    )

    const renderTabs = useCallback(() => {
        return [
            {
                title: 'Active Requests',
                content: memoTableActive,
                key: 'activeRequests',
                hasPadding: false,
            },
            {
                title: 'Past Requests',
                content: memoTableClosed,
                key: 'pastRequests',
                hasPadding: false,
            },
            {
                title: 'Service Estimates',
                content: memoTableEstimate,
                key: 'estimateRequests',
                hasPadding: false,
            },
        ]
    }, [
        filteredOpenRequests,
        filterOpen,
        estimateFilterOpen,
        filteredClosedRequests,
        filteredEstimateRequests,
        pendingRequests,
    ])

    return (
        <ContentLayout>
            <MainHeading
                title="Requests"
                actions={[
                    <Button
                        className="flex items-center justify-center md:w-auto w-full"
                        key="createRequest"
                        text="Create Requests"
                        onClick={() =>
                            history.push(AppRoutes.createRequestStepper())
                        }
                        variant="primary"
                        icon={
                            <DocumentAddIcon className="text-yellow stroke-current stroke-2 w-6 h-6 mr-2" />
                        }
                    />,
                ]}
            />
            <TabPanel
                firstTab={firstTab}
                onChangeTab={handleTabChange}
                background="bg-white"
                tabs={renderTabs()}
            />
            <SuccessActionMessage />
        </ContentLayout>
    )
}

RequestsPage.propTypes = {
    history: PropTypes.shape({
        push: PropTypes.func,
    }),
}

export default RequestsPage
